import React, { useState, useEffect } from 'react';
import { Button, Space, Typography, Card, Row, Col, Spin } from 'antd';
import { PlusOutlined, AppstoreOutlined, TableOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { usePartners, useDeletePartner } from '../hooks';
import { PartnerTable, PartnerFilters, PartnerCard } from '../components/partners';
import { buildFilterParams } from '../utils/helpers';

const { Title } = Typography;

const PartnersList = () => {
  const history = useHistory();
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [filters, setFilters] = useState({
    search: '',
    status: null,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
  });

  const queryParams = {
    ...buildFilterParams(filters),
    ...pagination,
  };

  const { data, isLoading, refetch } = usePartners(queryParams);
  const deletePartner = useDeletePartner();

  useEffect(() => {
    refetch();
  }, [filters, pagination, refetch]);

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      status: null,
    });
    setPagination({ page: 1, per_page: 10 });
  };

  const handlePaginationChange = (newPagination) => {
    setPagination((prev) => ({ ...prev, ...newPagination }));
  };

  const handleDelete = async (id) => {
    await deletePartner.mutateAsync(id);
    refetch();
  };

  const partners = data?.data || [];
  const paginationData = data?.meta || data?.pagination;

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              B2B Partners
            </Title>
          </Col>
          <Col>
            <Space>
              <Button.Group>
                <Button
                  icon={<TableOutlined />}
                  type={viewMode === 'table' ? 'primary' : 'default'}
                  onClick={() => setViewMode('table')}
                  aria-label="Table view"
                >
                  Table
                </Button>
                <Button
                  icon={<AppstoreOutlined />}
                  type={viewMode === 'grid' ? 'primary' : 'default'}
                  onClick={() => setViewMode('grid')}
                  aria-label="Grid view"
                >
                  Grid
                </Button>
              </Button.Group>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => history.push('/b2b/partners/new')}
              >
                Add Partner
              </Button>
            </Space>
          </Col>
        </Row>

        <PartnerFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onReset={handleResetFilters}
        />

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <Spin size="large" tip="Loading partners..." />
          </div>
        ) : viewMode === 'table' ? (
          <PartnerTable
            data={partners}
            loading={isLoading}
            pagination={paginationData}
            onPaginationChange={handlePaginationChange}
            onDelete={handleDelete}
          />
        ) : (
          <Row gutter={[16, 16]}>
            {partners.map((partner) => (
              <Col key={partner.id} xs={24} sm={12} md={8} lg={6}>
                <PartnerCard partner={partner} />
              </Col>
            ))}
          </Row>
        )}
      </Card>
    </div>
  );
};

export default PartnersList;